import {useCallback, useEffect, useRef, useState} from 'react'
import {getFullscreenSupport} from './fullscreenSupport'

type WebkitVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
  webkitDisplayingFullscreen?: boolean
}

interface UseTileFullscreenReturn {
  isFullscreen: boolean
  canFullscreen: boolean
  toggleFullscreen: (e: React.MouseEvent) => void
  exitFullscreen: () => void
}

export function useTileFullscreen(
  tileRef: React.RefObject<HTMLDivElement | null>,
): UseTileFullscreenReturn {
  const support = useRef(getFullscreenSupport())
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    if (support.current === 'standard') {
      const handleChange = () => {
        setIsFullscreen(document.fullscreenElement === tileRef.current)
      }
      document.addEventListener('fullscreenchange', handleChange)
      return () => document.removeEventListener('fullscreenchange', handleChange)
    }

    if (support.current === 'video-only') {
      const videoEl = tileRef.current?.querySelector('video') as WebkitVideoElement | null
      if (!videoEl) return
      const handleChange = () => {
        setIsFullscreen(!!videoEl.webkitDisplayingFullscreen)
      }
      videoEl.addEventListener('webkitfullscreenchange', handleChange)
      return () => videoEl.removeEventListener('webkitfullscreenchange', handleChange)
    }
  }, [tileRef])

  const exitFullscreen = useCallback(() => {
    if (support.current === 'standard') {
      document.exitFullscreen()
    } else if (support.current === 'video-only') {
      const videoEl = tileRef.current?.querySelector('video') as WebkitVideoElement | null
      videoEl?.webkitExitFullscreen?.()
    }
  }, [tileRef])

  const toggleFullscreen = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (support.current === 'standard') {
        if (!document.fullscreenElement) {
          tileRef.current?.requestFullscreen()
        } else {
          document.exitFullscreen()
        }
        return
      }
      if (support.current === 'video-only') {
        const videoEl = tileRef.current?.querySelector('video') as WebkitVideoElement | null
        if (!videoEl) return
        if (videoEl.webkitDisplayingFullscreen) {
          videoEl.webkitExitFullscreen?.()
        } else {
          videoEl.webkitEnterFullscreen?.()
        }
      }
    },
    [tileRef],
  )

  return {
    isFullscreen,
    canFullscreen: support.current !== 'none',
    toggleFullscreen,
    exitFullscreen,
  }
}
